import React, { useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { User, Building, Phone, Briefcase, CheckCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useOnboarding } from '../../contexts/OnboardingContext';

const ProfileSetupStep = ({ onNext, onSave }) => {
  const { completeOnboardingStep } = useAuth();
  const { completeStep, updateUserData, userData } = useOnboarding();
  
  const [formData, setFormData] = useState({
    jobTitle: userData.jobTitle || '',
    companyName: userData.companyName || '',
    phoneNumber: userData.phoneNumber || '',
    useCase: userData.useCase || ''
  });

  const [errors, setErrors] = useState({});

  // Sync formData with userData when userData changes
  React.useEffect(() => {
    setFormData({
      jobTitle: userData.jobTitle || '',
      companyName: userData.companyName || '',
      phoneNumber: userData.phoneNumber || '',
      useCase: userData.useCase || ''
    });
  }, [userData.jobTitle, userData.companyName, userData.phoneNumber, userData.useCase]);

  const useCaseOptions = [
    { value: 'Legal', label: 'Legal & Compliance', icon: '⚖️' },
    { value: 'HR', label: 'Human Resources', icon: '👥' },
    { value: 'Sales', label: 'Sales & Business Development', icon: '💼' },
    { value: 'Personal', label: 'Personal Use', icon: '👤' },
    { value: 'Other', label: 'Other', icon: '🔧' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = useCallback(() => {
    const newErrors = {};
    
    if (!formData.jobTitle.trim()) {
      newErrors.jobTitle = 'Job title is required';
    }
    
    if (!formData.companyName.trim()) {
      newErrors.companyName = 'Company name is required';
    }
    
    if (!formData.useCase) {
      newErrors.useCase = 'Please select your primary use case';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState('');

  // Memoized save function to prevent stale closures
  const handleSave = useCallback(async () => {
    if (validateForm()) {
      setIsLoading(true);
      setApiError('');
      
      try {
        // Save to backend first
        await completeOnboardingStep('profile', formData);
        // Only update local state and proceed if backend save succeeds
        updateUserData(formData);
        completeStep('profile', formData);
        return true; // Success
      } catch (error) {
        console.error('Error saving profile data:', error);
        setApiError('Failed to save profile data. Please check your connection and try again.');
        return false; // Failed
      } finally {
        setIsLoading(false);
      }
    }
    return false; // Validation failed
  }, [formData, validateForm, completeOnboardingStep, updateUserData, completeStep]);

  // Expose save function to parent via onSave prop
  React.useEffect(() => {
    if (onSave) {
      onSave(handleSave);
    }
  }, [onSave, handleSave]);

  return (
    <div className="p-8 max-w-2xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="text-center mb-8"
      >
        <h2 className="text-3xl font-bold text-gray-900 mb-4">
          Tell us about yourself
        </h2>
        <p className="text-lg text-gray-600">
          This helps us personalize your SignPact experience and provide better recommendations.
        </p>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
        className="space-y-6"
      >
        {/* Job Title */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <User className="inline w-4 h-4 mr-2" />
            Job Title *
          </label>
          <input
            type="text"
            value={formData.jobTitle}
            onChange={(e) => handleInputChange('jobTitle', e.target.value)}
            placeholder="e.g., Senior Legal Counsel, HR Manager"
            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
              errors.jobTitle ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {errors.jobTitle && (
            <p className="mt-1 text-sm text-red-600">{errors.jobTitle}</p>
          )}
        </div>

        {/* Company Name */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <Building className="inline w-4 h-4 mr-2" />
            Company Name *
          </label>
          <input
            type="text"
            value={formData.companyName}
            onChange={(e) => handleInputChange('companyName', e.target.value)}
            placeholder="e.g., Acme Corp, Law Firm LLC"
            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
              errors.companyName ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {errors.companyName && (
            <p className="mt-1 text-sm text-red-600">{errors.companyName}</p>
          )}
        </div>

        {/* Phone Number */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <Phone className="inline w-4 h-4 mr-2" />
            Phone Number <span className="text-gray-500">(Optional)</span>
          </label>
          <input
            type="tel"
            value={formData.phoneNumber}
            onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
            placeholder="e.g., +1 (555) 123-4567"
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
          />
        </div>

        {/* Use Case */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">
            <Briefcase className="inline w-4 h-4 mr-2" />
            Primary Use Case *
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {useCaseOptions.map((option) => (
              <label
                key={option.value}
                className={`flex items-center p-4 border rounded-lg cursor-pointer transition-colors ${
                  formData.useCase === option.value
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-300 hover:border-gray-400'
                }`}
              >
                <input
                  type="radio"
                  name="useCase"
                  value={option.value}
                  checked={formData.useCase === option.value}
                  onChange={(e) => handleInputChange('useCase', e.target.value)}
                  className="sr-only"
                />
                <span className="text-2xl mr-3">{option.icon}</span>
                <div className="flex-1">
                  <div className="font-medium text-gray-900">{option.label}</div>
                </div>
                {formData.useCase === option.value && (
                  <CheckCircle className="w-5 h-5 text-blue-500" />
                )}
              </label>
            ))}
          </div>
          {errors.useCase && (
            <p className="mt-1 text-sm text-red-600">{errors.useCase}</p>
          )}
        </div>
      </motion.div>

      {apiError && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="mt-8 text-center"
        >
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-600">{apiError}</p>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default ProfileSetupStep;